import React from "react";
import styles from "./LandingPage.module.css";

const LINKS = [
  ["Information We Collect", "#collect"],
  ["What We Do NOT Collect", "#no-collect"],
  ["How We Use Information", "#use"],
  ["Analytics and Cookies", "#cookies"],
  ["Data Sharing and Disclosure", "#sharing"],
  ["Data Retention", "#retention"],
  ["Data Security", "#security"],
  ["International Users", "#international"],
  ["Your Rights and Choices", "#rights"],
  ["Children’s Privacy", "#children"],
  ["Changes to This Policy", "#changes"],
  ["Contact Us", "#contact"],
];

const PrivacyPolicy = () => {
  return (
    <main className={styles.main}>
      <section className={styles.section}>
        <div className={styles.sectionInner}>
          <header className={styles.legalHeader}>
            <div className={styles.sectionLabel}>LEGAL</div>
            <h1 className={styles.legalTitle}>Privacy Policy</h1>
            <p className={styles.legalMeta}>Last Updated: [Insert Date]</p>
          </header>

          <div className={styles.legalLayout}>
            <aside className={styles.legalSidebar}>
              <div className={styles.legalSidebarPanel}>
                <div className={styles.legalSidebarTitle}>On this page</div>
                <ul className={styles.legalSidebarList}>
                  {LINKS.map(([label, href]) => (
                    <li key={href}>
                      <a className={`${styles.legalSidebarLink} ${styles.focusRing}`} href={href}>
                        {label}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            </aside>

            <div className={styles.legalPanel}>
              <section id="intro">
                <p className={styles.legalBody}>
                  This Privacy Policy explains how we collect, use, and protect information when you use this
                  application (“the Platform”).
                </p>
                <p className={styles.legalBody}>
                  By accessing or using the Platform, you consent to the practices described below.
                </p>
              </section>

              <section id="collect" className={styles.legalClause}>
                <h2 className={styles.legalH2}>1. Information We Collect</h2>
                <p className={styles.legalBody}>A. Information You Provide</p>
                <p className={styles.legalBody}>We may collect information you voluntarily provide, such as:</p>
                <ul className={styles.legalList}>
                  <li>Email address (for account access, updates, or support)</li>
                  <li>Account preferences and settings</li>
                  <li>Communications with us (e.g., support inquiries)</li>
                </ul>
                <p className={styles.legalBody}>
                  We do not require you to provide financial account credentials, brokerage logins, or payment details
                  to use core analytical features.
                </p>
                <p className={styles.legalBody}>B. Automatically Collected Information</p>
                <p className={styles.legalBody}>
                  When you use the Platform, we may automatically collect limited technical data, including:
                </p>
                <ul className={styles.legalList}>
                  <li>IP address</li>
                  <li>Browser type and device information</li>
                  <li>Operating system</li>
                  <li>Pages viewed and feature usage</li>
                  <li>Timestamps and session duration</li>
                </ul>
                <p className={styles.legalBody}>
                  This information is used for performance monitoring, security, and product improvement.
                </p>
                <p className={styles.legalBody}>C. Market and Analytical Data</p>
                <p className={styles.legalBody}>The Platform processes:</p>
                <ul className={styles.legalList}>
                  <li>Public market data</li>
                  <li>Regulatory data (e.g., CFTC reports)</li>
                  <li>Price history and derived analytics</li>
                </ul>
                <p className={styles.legalBody}>
                  This data does not identify you personally and is used solely to generate analytical outputs.
                </p>
              </section>

              <section id="no-collect" className={styles.legalClause}>
                <h2 className={styles.legalH2}>2. What We Do NOT Collect</h2>
                <p className={styles.legalBody}>We do not collect or store:</p>
                <ul className={styles.legalList}>
                  <li>Brokerage account credentials</li>
                  <li>Bank account or payment card information</li>
                  <li>Social Security numbers or government IDs</li>
                  <li>Trade execution details from your brokerage</li>
                  <li>Personal financial position or net worth</li>
                </ul>
                <p className={styles.legalBody}>The Platform does not connect to brokerage accounts or place trades.</p>
              </section>

              <section id="use" className={styles.legalClause}>
                <h2 className={styles.legalH2}>3. How We Use Information</h2>
                <p className={styles.legalBody}>We use collected information to:</p>
                <ul className={styles.legalList}>
                  <li>Operate and maintain the Platform</li>
                  <li>Provide analytical features and visualizations</li>
                  <li>Improve performance, reliability, and usability</li>
                  <li>Communicate service-related updates</li>
                  <li>Monitor usage patterns to improve product design</li>
                  <li>Protect against fraud, abuse, or security threats</li>
                </ul>
                <p className={styles.legalBody}>We do not use your data to provide personalized investment advice.</p>
              </section>

              <section id="cookies" className={styles.legalClause}>
                <h2 className={styles.legalH2}>4. Analytics and Cookies</h2>
                <p className={styles.legalBody}>We may use cookies or similar technologies to:</p>
                <ul className={styles.legalList}>
                  <li>Maintain session state</li>
                  <li>Remember user preferences</li>
                  <li>Understand aggregate usage patterns</li>
                </ul>
                <p className={styles.legalBody}>
                  Cookies are functional and analytical only. We do not use cookies for targeted advertising or
                  behavioral profiling.
                </p>
                <p className={styles.legalBody}>
                  You may disable cookies in your browser, though some features may not function correctly.
                </p>
              </section>

              <section id="sharing" className={styles.legalClause}>
                <h2 className={styles.legalH2}>5. Data Sharing and Disclosure</h2>
                <p className={styles.legalBody}>We do not sell, rent, or trade your personal information.</p>
                <p className={styles.legalBody}>We may share limited information only:</p>
                <ul className={styles.legalList}>
                  <li>With service providers who support hosting, analytics, or infrastructure (under confidentiality obligations)</li>
                  <li>If required by law, regulation, or legal process</li>
                  <li>To protect the security or integrity of the Platform</li>
                </ul>
                <p className={styles.legalBody}>
                  All third-party providers are restricted from using your data for their own purposes.
                </p>
              </section>

              <section id="retention" className={styles.legalClause}>
                <h2 className={styles.legalH2}>6. Data Retention</h2>
                <p className={styles.legalBody}>
                  We retain personal information only as long as necessary to:
                </p>
                <ul className={styles.legalList}>
                  <li>Provide the Platform</li>
                  <li>Fulfill legal or operational requirements</li>
                  <li>Resolve disputes or enforce agreements</li>
                </ul>
                <p className={styles.legalBody}>
                  Aggregated and anonymized analytical data may be retained for research and product improvement.
                </p>
              </section>

              <section id="security" className={styles.legalClause}>
                <h2 className={styles.legalH2}>7. Data Security</h2>
                <p className={styles.legalBody}>
                  We implement reasonable administrative, technical, and organizational safeguards to protect your
                  information.
                </p>
                <p className={styles.legalBody}>
                  However, no system can be guaranteed 100% secure. You use the Platform at your own risk.
                </p>
              </section>

              <section id="international" className={styles.legalClause}>
                <h2 className={styles.legalH2}>8. International Users</h2>
                <p className={styles.legalBody}>
                  If you access the Platform from outside your country of residence, your information may be processed
                  in jurisdictions with different data protection laws.
                </p>
                <p className={styles.legalBody}>By using the Platform, you consent to such processing.</p>
              </section>

              <section id="rights" className={styles.legalClause}>
                <h2 className={styles.legalH2}>9. Your Rights and Choices</h2>
                <p className={styles.legalBody}>
                  Depending on your location, you may have the right to:
                </p>
                <ul className={styles.legalList}>
                  <li>Access the personal data we hold about you</li>
                  <li>Request correction or deletion of your data</li>
                  <li>Object to or restrict certain processing</li>
                </ul>
                <p className={styles.legalBody}>To exercise these rights, contact us at the address below.</p>
              </section>

              <section id="children" className={styles.legalClause}>
                <h2 className={styles.legalH2}>10. Children’s Privacy</h2>
                <p className={styles.legalBody}>
                  The Platform is not intended for individuals under the age of 18.
                </p>
                <p className={styles.legalBody}>
                  We do not knowingly collect personal data from children.
                </p>
              </section>

              <section id="changes" className={styles.legalClause}>
                <h2 className={styles.legalH2}>11. Changes to This Policy</h2>
                <p className={styles.legalBody}>
                  We may update this Privacy Policy from time to time.
                </p>
                <p className={styles.legalBody}>
                  When we do, we will update the “Last Updated” date. Continued use of the Platform constitutes
                  acceptance of the revised policy.
                </p>
              </section>

              <section id="contact" className={styles.legalClause}>
                <h2 className={styles.legalH2}>12. Contact Us</h2>
                <p className={styles.legalBody}>
                  If you have questions about this Privacy Policy or our data practices, contact:
                </p>
                <p className={styles.legalBody}>Deng Software LLC</p>
                <p className={styles.legalBody}>info@deng-consulting.com</p>
              </section>
            </div>
          </div>

          <div className={styles.legalFooter}>
            Educational analytics only. Not financial advice. No trade execution.
          </div>
        </div>
      </section>
    </main>
  );
};

export default PrivacyPolicy;
